// plugins/kubernetes/components/kaapi/machines/MachineErrorModal.tsx
import { makeStyles } from '@material-ui/styles'
import Alert from 'core/components/Alert'
import CodeBlock from 'core/components/CodeBlock'
import Button from 'core/elements/button/Button'
import Modal from 'core/elements/modal/Modal'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { IVmDetailsPageTabs } from 'app/plugins/openstack/components/vms/vm-details/model'
import React, { useCallback, useEffect, useMemo } from 'react'
import { extractErrorSummary } from './errorSummary'

const useStyles = makeStyles<Theme>((theme) => ({
  errorBlock: {
    color: theme.components.alert.error.border,
    backgroundColor: theme.components.alert.error.background,
    maxHeight: 320,
    overflow: 'auto',
  },
  reason: {
    color: theme.palette.grey[700],
    marginBottom: theme.spacing(1),
  },
  summary: {
    marginBottom: theme.spacing(1),
  },
  summaryText: {
    fontWeight: 500,
  },
  remediation: {
    marginTop: theme.spacing(1),
  },
  copySuccessText: {
    color: theme.palette.success.main,
    marginTop: theme.spacing(1),
  },
}))

interface MachineErrorModalProps {
  machineName: string
  reason?: string
  message: string
  instanceId?: string
  remediation?: string
  onClose: () => void
}

const MachineErrorModal = ({
  machineName,
  reason,
  message,
  instanceId,
  remediation,
  onClose,
}: MachineErrorModalProps) => {
  const classes = useStyles()
  const [copied, setCopied] = React.useState(false)
  const summary = useMemo(() => extractErrorSummary(message), [message])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  useEffect(() => {
    if (!copied) return undefined
    const timeoutId = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timeoutId)
  }, [copied])

  const handleErrorCopy = useCallback(async () => {
    const errorText = reason ? `${reason}: ${message}` : message
    const copyText = summary ? `${summary}\n${errorText}` : errorText
    await navigator.clipboard.writeText(copyText)
    setCopied(true)
  }, [summary, reason, message])

  const handleOpenConsole = useCallback(() => {
    if (!instanceId) return
    const consoleUrl = routeManager.routes.openstack.vmDetails.path({
      id: instanceId,
      tab: IVmDetailsPageTabs.Overview,
    })
    window.open(consoleUrl, '_blank')
  }, [instanceId])

  return (
    <Modal
      title={`Error Details - ${machineName}`}
      onClose={onClose}
      maxWidth={1000}
      panel="dialog"
      footer={
        <>
          {!!instanceId && (
            <Button variant="secondary" onClick={handleOpenConsole}>
              Open VM Console
            </Button>
          )}
          <Button variant="secondary" onClick={handleErrorCopy}>
            {copied ? 'Copied!' : 'Copy'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
      open
    >
      {!!reason && (
        <Text variant="caption1" className={classes.reason}>
          {reason}
        </Text>
      )}
      {!!summary && (
        <Alert
          variant="error"
          message={
            <Text variant="body1" className={classes.summaryText}>
              {summary}
            </Text>
          }
          className={classes.summary}
        />
      )}
      <CodeBlock className={classes.errorBlock}>{message}</CodeBlock>
      {!!remediation && (
        <Text variant="body2" className={classes.remediation}>
          {remediation}
        </Text>
      )}
    </Modal>
  )
}

export default MachineErrorModal
